import { FormGroup } from '@angular/forms';
import { FORM_ELEMENT_KEYS } from '@app/misc/mobile-view-builder/templates/form-elements';

export enum CARD_STATUS {
  active = 'active',
  unassigned = 'unassigned',
  assigned = 'assigned',
  inactive = 'inactive',
  frozen = 'frozen',
  suspended = 'suspended',
}

export enum TIME_LIMIT_OPTIONS {
  everyday = 'everyday',
  custom = 'custom',
}

export enum SPEND_CATEGORY_OPTIONS {
  all = 'all',
  specific = 'specific',
}

export interface IDayOfWeek {
  id: string;
  label: string;
  shortLabel: string;
  selected: boolean;
}

export type CardStatus = keyof typeof CARD_STATUS;

export const SPEND_PROFILE_FORM_KEYS = {
  name: 'name',
  isDefault: 'isDefault',
  creditLimit: 'creditLimit',
  transactionCap: 'transactionCap',
  creditLimitPerDay: 'creditLimitPerDay',
  creditLimitPerWeek: 'creditLimitPerWeek',
  creditLimitPerMonth: 'creditLimitPerMonth',
  timeLimit: 'timeLimit',
  enableDays: 'enableDays',
  enableStartTime: 'enableStartTime',
  enableEndTime: 'enableEndTime',
  spendCategory: 'spendCategory',
  spendCategoryIds: 'spendCategoryIds',
};

export const SPEND_PROFILE_FORM_CONFIG = {
  [SPEND_PROFILE_FORM_KEYS.name]: {
    label: $localize`Profile name`,
    placeholder: $localize`Profile name`,
    requestKey: 'name',
    responseKey: 'name',
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
  },
  [SPEND_PROFILE_FORM_KEYS.isDefault]: {
    label: $localize`Set as default`,
    description: 'This profile will be applied to all new card activations. Existing cards will not be affected.',
    requestKey: 'is_default',
    responseKey: 'isDefault',
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [SPEND_PROFILE_FORM_KEYS.creditLimitPerDay]: {
    label: $localize`Per day`,
    placeholder: '--',
    requestKey: 'daily_spend_limit_in_cents',
    responseKey: 'dailySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS.creditLimitPerWeek]: {
    label: $localize`Per week (Sun-Sat)`,
    placeholder: '--',
    requestKey: 'weekly_spend_limit_in_cents',
    responseKey: 'weeklySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS.creditLimitPerMonth]: {
    label: $localize`Per calendar month`,
    placeholder: '--',
    requestKey: 'monthly_spend_limit_in_cents',
    responseKey: 'monthlySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS.creditLimit]: {
    label: $localize`Per billing cycle`,
    placeholder: '--',
    requestKey: 'billing_cycle_spend_limit_in_cents',
    responseKey: 'billingCycleSpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS.transactionCap]: {
    label: $localize`Per transaction`,
    placeholder: '--',
    requestKey: 'transaction_limit_in_cents',
    responseKey: 'transactionLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS.timeLimit]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: TIME_LIMIT_OPTIONS.everyday,
        label: $localize`Everyday`,
        description: $localize`Cards will work at all times.`,
      },
      {
        key: TIME_LIMIT_OPTIONS.custom,
        label: $localize`Custom`,
        description: $localize`Cards will only work during a set schedule.`,
      },
    ],
  },
  [SPEND_PROFILE_FORM_KEYS.enableDays]: {
    initialValue: [],
    requestKey: 'enable_days',
    responseKey: 'enableDays',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS.enableStartTime]: {
    requestKey: 'enable_start_time',
    responseKey: 'enableStartTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS.enableEndTime]: {
    requestKey: 'enable_end_time',
    responseKey: 'enableEndTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS.spendCategory]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: SPEND_CATEGORY_OPTIONS.all,
        label: $localize`Allow all categories`,
      },
      {
        key: SPEND_CATEGORY_OPTIONS.specific,
        label: $localize`Allow specific categories`,
      },
    ],
  },
  [SPEND_PROFILE_FORM_KEYS.spendCategoryIds]: {
    initialValue: [],
    requestKey: 'spend_category_ids',
    responseKey: 'spendCategoryIds',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
};

export const SPEND_PROFILE_FORM_KEYS_V2 = {
  name: 'name',
  isDefault: 'isDefault',
  creditLimit: 'creditLimit',
  transactionCap: 'transactionCap',
  creditLimitPerDay: 'creditLimitPerDay',
  creditLimitPerWeek: 'creditLimitPerWeek',
  creditLimitPerMonth: 'creditLimitPerMonth',
  timeLimit: 'timeLimit',
  enableDays: 'enableDays',
  enableStartTime: 'enableStartTime',
  enableEndTime: 'enableEndTime',
  spendCategory: 'spendCategory',
  spendCategoryIds: 'spendCategoryIds',
  atmWithdrawalEnabled: 'atmWithdrawalEnabled',
  dailyWithdrawalLimit: 'dailyWithdrawalLimit',
  weeklyWithdrawalLimit: 'weeklyWithdrawalLimit',
};

export const SPEND_PROFILE_FORM_CONFIG_V2 = {
  [SPEND_PROFILE_FORM_KEYS_V2.name]: {
    label: $localize`Profile name`,
    placeholder: $localize`Profile name`,
    requestKey: 'name',
    responseKey: 'name',
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.isDefault]: {
    label: $localize`Set as default`,
    description: $localize`This profile will be applied to all new card activations. Existing cards will not be affected.`,
    requestKey: 'is_default',
    responseKey: 'isDefault',
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.creditLimitPerDay]: {
    label: $localize`Per day`,
    placeholder: '--',
    requestKey: 'daily_spend_limit_in_cents',
    responseKey: 'dailySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.creditLimitPerWeek]: {
    label: $localize`Per week (Sun-Sat)`,
    placeholder: '--',
    requestKey: 'weekly_spend_limit_in_cents',
    responseKey: 'weeklySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.creditLimitPerMonth]: {
    label: $localize`Per calendar month`,
    placeholder: '--',
    requestKey: 'monthly_spend_limit_in_cents',
    responseKey: 'monthlySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.creditLimit]: {
    label: $localize`Per billing cycle`,
    placeholder: '--',
    requestKey: 'billing_cycle_spend_limit_in_cents',
    responseKey: 'billingCycleSpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.transactionCap]: {
    label: $localize`Per transaction`,
    placeholder: '--',
    requestKey: 'transaction_limit_in_cents',
    responseKey: 'transactionLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.timeLimit]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: TIME_LIMIT_OPTIONS.everyday,
        label: $localize`Everyday`,
        description: $localize`Cards will work at all times.`,
      },
      {
        key: TIME_LIMIT_OPTIONS.custom,
        label: $localize`Custom`,
        description: $localize`Cards will only work during a set schedule.`,
      },
    ],
  },
  [SPEND_PROFILE_FORM_KEYS_V2.enableDays]: {
    initialValue: [],
    requestKey: 'enable_days',
    responseKey: 'enableDays',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.enableStartTime]: {
    requestKey: 'enable_start_time',
    responseKey: 'enableStartTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.enableEndTime]: {
    requestKey: 'enable_end_time',
    responseKey: 'enableEndTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.spendCategory]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: SPEND_CATEGORY_OPTIONS.all,
        label: $localize`Allow all categories`,
      },
      {
        key: SPEND_CATEGORY_OPTIONS.specific,
        label: $localize`Allow specific categories`,
      },
    ],
  },
  [SPEND_PROFILE_FORM_KEYS_V2.spendCategoryIds]: {
    initialValue: [],
    requestKey: 'spend_category_ids',
    responseKey: 'spendCategoryIds',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.atmWithdrawalEnabled]: {
    requestKey: 'atm_withdrawal_enabled',
    responseKey: 'atmWithdrawalEnabled',
    type: FORM_ELEMENT_KEYS.TOGGLE,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.dailyWithdrawalLimit]: {
    label: $localize`Daily withdrawal limit`,
    placeholder: '--',
    requestKey: 'daily_withdrawal_limit_in_cents',
    responseKey: 'dailyWithdrawalLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_PROFILE_FORM_KEYS_V2.weeklyWithdrawalLimit]: {
    label: $localize`Weekly withdrawal limit`,
    placeholder: '--',
    requestKey: 'weekly_withdrawal_limit_in_cents',
    responseKey: 'weeklyWithdrawalLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
};

export const CASH_ADVANCE_KEYS_MAP = {
  dailyWithdrawalLimitInCents: $localize`daily withdrawal limit`,
  weeklyWithdrawalLimitInCents: $localize`weekly withdrawal limit`,
};

export const TRANSACTION_LIMITS_KEYS_MAP = {
  dailySpendLimitInCents: $localize`daily`,
  weeklySpendLimitInCents: $localize`weekly`,
  monthlySpendLimitInCents: $localize`monthly`,
  transactionLimitInCents: $localize`/ transaction`,
  billingCycleSpendLimitInCents: $localize`/ billing cycle`,
};

export enum CARD_IDS {
  PROFILE_DETAILS = 'profileDetails',
  TIME_LIMITS = 'timeLimits',
  SPEND_LIMITS = 'spendLimits',
  CASH_ADVANCE = 'cashAdvance',
  CATEGORY_LIMITS = 'categoryLimits',
  OVERRIDE_DETAILS = 'overrideDetails',
  SECURITY_CONTROLS = 'securityControls',
}

export const DEFAULT_CUSTOM_DAY_RANGE = [0, 1, 1, 1, 1, 1, 0];

export const GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS = {
  [CARD_IDS.PROFILE_DETAILS] : {
    name: 'name',
    isDefault: 'isDefault',
  },
  [CARD_IDS.TIME_LIMITS]: {
    timeLimit: 'timeLimit',
    enableDays: 'enableDays',
    enableStartTime: 'enableStartTime',
    enableEndTime: 'enableEndTime',
  },
  [CARD_IDS.SPEND_LIMITS]: {
    creditLimit: 'creditLimit',
    transactionCap: 'transactionCap',
    creditLimitPerDay: 'creditLimitPerDay',
    creditLimitPerWeek: 'creditLimitPerWeek',
    creditLimitPerMonth: 'creditLimitPerMonth',
  },
  [CARD_IDS.CASH_ADVANCE]: {
    atmWithdrawalEnabled: 'atmWithdrawalEnabled',
    dailyWithdrawalLimit: 'dailyWithdrawalLimit',
    weeklyWithdrawalLimit: 'weeklyWithdrawalLimit',
  },
  [CARD_IDS.CATEGORY_LIMITS]: {
    spendCategory: 'spendCategory',
    spendCategoryIds: 'spendCategoryIds',
    categorySpecificSpendLimits: 'categorySpecificSpendLimits',
  },
  [CARD_IDS.OVERRIDE_DETAILS] : {
    duration: 'duration',
    startDate: 'startDate',
    startTime: 'startTime',
    endDate: 'endDate',
    endTime: 'endTime',
    reason: 'reason',
  },
  [CARD_IDS.SECURITY_CONTROLS]: {
    mobileBasedUnlock: 'mobileBasedUnlock',
    vehicleProximityBasedDeclineEnabled: 'vehicleProximityBasedDeclineEnabled',

    // TODO: remove it till temp override launch if needed
    fuelLevelExceedsThresholdEnabled: 'fuelLevelExceedsThresholdEnabled',
    fuelLevelPercentage: 'fuelLevelPercentage',
  },
};

export const GRANULAR_CONTROL_SPEND_PROFILE_FORM_CONFIG = {
  [CARD_IDS.PROFILE_DETAILS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.profileDetails.name]: {
      label: $localize`Profile name`,
      placeholder: $localize`Profile name`,
      requestKey: 'name',
      responseKey: 'name',
      type: FORM_ELEMENT_KEYS.TEXT,
      required: true,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.profileDetails.isDefault]: {
      label: $localize`Set as default`,
      description: $localize`This profile will be applied to all new card activations. Existing cards will not be affected.`,
      requestKey: 'is_default',
      responseKey: 'isDefault',
      type: FORM_ELEMENT_KEYS.TOGGLE,
      required: false,
    },
  },
  [CARD_IDS.TIME_LIMITS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.timeLimits.timeLimit]: {
      type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
      initialValue: TIME_LIMIT_OPTIONS.everyday,
      options: [
        {
          key: TIME_LIMIT_OPTIONS.everyday,
          label: $localize`Everyday`,
          description: $localize`Cards will work at all times.`,
        },
        {
          key: TIME_LIMIT_OPTIONS.custom,
          label: $localize`Custom`,
          description: $localize`Cards will only work during a set schedule.`,
        },
      ],
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.timeLimits.enableDays]: {
      initialValue: [...DEFAULT_CUSTOM_DAY_RANGE],
      requestKey: 'enable_days',
      responseKey: 'enableDays',
      type: FORM_ELEMENT_KEYS.MULTI_SELECT,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.timeLimits.enableStartTime]: {
      requestKey: 'enable_start_time',
      responseKey: 'enableStartTime',
      type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.timeLimits.enableEndTime]: {
      requestKey: 'enable_end_time',
      responseKey: 'enableEndTime',
      type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    },
  },
  [CARD_IDS.SPEND_LIMITS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.spendLimits.creditLimitPerDay]: {
      label: $localize`Per day`,
      comparisonTooltipLabel: $localize`Per day`,
      errorMessageLabel: $localize`daily`,
      requestKey: 'daily_spend_limit_in_cents',
      responseKey: 'dailySpendLimitInCents',
      responseKeySecondary: 'dailySpendLimitInDollars',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.spendLimits.creditLimitPerWeek]: {
      label: $localize`Per week (Sun-Sat)`,
      comparisonTooltipLabel: $localize`Per week`,
      errorMessageLabel: $localize`weekly`,
      requestKey: 'weekly_spend_limit_in_cents',
      responseKey: 'weeklySpendLimitInCents',
      responseKeySecondary: 'weeklySpendLimitInDollars',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.spendLimits.creditLimitPerMonth]: {
      label: $localize`Per calendar month`,
      comparisonTooltipLabel: $localize`Per month`,
      errorMessageLabel: $localize`monthly`,
      requestKey: 'monthly_spend_limit_in_cents',
      responseKey: 'monthlySpendLimitInCents',
      responseKeySecondary: 'monthlySpendLimitInDollars',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.spendLimits.creditLimit]: {
      label: $localize`Per billing cycle`,
      comparisonTooltipLabel: $localize`Per billing cycle`,
      errorMessageLabel: $localize`billing cycle`,
      requestKey: 'billing_cycle_spend_limit_in_cents',
      responseKey: 'billingCycleSpendLimitInCents',
      responseKeySecondary: 'billingCycleSpendLimitInDollars',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.spendLimits.transactionCap]: {
      label: $localize`Per transaction`,
      comparisonTooltipLabel: $localize`Per transaction`,
      errorMessageLabel: $localize`transaction`,
      requestKey: 'transaction_limit_in_cents',
      responseKey: 'transactionLimitInCents',
      responseKeySecondary: 'transactionLimitInDollars',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
  },
  [CARD_IDS.CASH_ADVANCE]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.cashAdvance.atmWithdrawalEnabled]: {
      requestKey: 'atm_withdrawal_enabled',
      responseKey: 'atmWithdrawalEnabled',
      type: FORM_ELEMENT_KEYS.TOGGLE,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.cashAdvance.dailyWithdrawalLimit]: {
      label: $localize`Daily withdrawal limit`,
      comparisonTooltipLabel: $localize`Daily limit`,
      requestKey: 'daily_withdrawal_limit_in_cents',
      responseKey: 'dailyWithdrawalLimitInCents',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.cashAdvance.weeklyWithdrawalLimit]: {
      label: $localize`Weekly withdrawal limit`,
      comparisonTooltipLabel: $localize`Weekly limit`,
      requestKey: 'weekly_withdrawal_limit_in_cents',
      responseKey: 'weeklyWithdrawalLimitInCents',
      type: FORM_ELEMENT_KEYS.NUMBER,
    },
  },
  [CARD_IDS.CATEGORY_LIMITS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.categoryLimits.spendCategory]: {
      type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
      initialValue: SPEND_CATEGORY_OPTIONS.all,
      options: [
        {
          key: SPEND_CATEGORY_OPTIONS.all,
          label: $localize`Allow all categories`,
        },
        {
          key: SPEND_CATEGORY_OPTIONS.specific,
          label: $localize`Allow specific categories`,
        },
      ],
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.categoryLimits.spendCategoryIds]: {
      initialValue: [],
      requestKey: 'spend_category_ids',
      responseKey: 'spendCategoryIds',
      type: FORM_ELEMENT_KEYS.MULTI_SELECT,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.categoryLimits.categorySpecificSpendLimits]: {
      type: FormGroup,
      requestKey: 'category_specific_spend_limits',
      responseKey: 'categorySpecificSpendLimits',
    },
  },
  [CARD_IDS.OVERRIDE_DETAILS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.duration]: {
      label: $localize`Duration`,
      placeholder: $localize`Select`,
      requestKey: 'duration',
      responseKey: 'duration',
      type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.startDate]: {
      label: $localize`Start date`,
      placeholder: $localize`Select`,
      requestKey: 'start_date',
      responseKey: 'startDate',
      type: FORM_ELEMENT_KEYS.DATE_TIME,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.startTime]: {
      label: $localize`Start time`,
      placeholder: $localize`Select`,
      requestKey: 'start_time',
      responseKey: 'startTime',
      type: FORM_ELEMENT_KEYS.DATE_TIME,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.endDate]: {
      label: $localize`End date`,
      placeholder: $localize`Select`,
      requestKey: 'end_date',
      responseKey: 'endDate',
      type: FORM_ELEMENT_KEYS.DATE_TIME,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.endTime]: {
      label: $localize`End time`,
      placeholder: $localize`Select`,
      requestKey: 'end_time',
      responseKey: 'endTime',
      type: FORM_ELEMENT_KEYS.DATE_TIME,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.overrideDetails.reason]: {
      label: $localize`Override reason`,
      requestKey: 'reason',
      responseKey: 'reason',
      type: FORM_ELEMENT_KEYS.TEXT,
      required: false,
    },
  },
  [CARD_IDS.SECURITY_CONTROLS]: {
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.securityControls.mobileBasedUnlock]: {
      label: $localize`Require mobile-based unlock for this card`,
      type: FORM_ELEMENT_KEYS.TOGGLE,
      required: false,
    },
    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.securityControls.vehicleProximityBasedDeclineEnabled]: {
      label: $localize`Vehicle location does not match the merchant location`,
      type: FORM_ELEMENT_KEYS.TOGGLE,
      required: false,
    },

    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.securityControls.fuelLevelExceedsThresholdEnabled]: {
      label: $localize`Fuel level is above`,
      type: FORM_ELEMENT_KEYS.TOGGLE,
      required: false,
    },

    [GRANULAR_CONTROL_SPEND_PROFILE_FORM_KEYS.securityControls.fuelLevelPercentage]: {
      label: ``,
      type: FORM_ELEMENT_KEYS.NUMBER,
      required: false,
    },
  },
};

export const SPEND_CONTROL_V2_FORM_KEYS = {
  cardStatus: 'cardStatus',
  creditLimit: 'creditLimit',
  transactionCap: 'transactionCap',
  creditLimitPerDay: 'creditLimitPerDay',
  creditLimitPerWeek: 'creditLimitPerWeek',
  creditLimitPerMonth: 'creditLimitPerMonth',
  timeLimit: 'timeLimit',
  enableDays: 'enableDays',
  enableStartTime: 'enableStartTime',
  enableEndTime: 'enableEndTime',
  spendCategory: 'spendCategory',
  spendCategoryIds: 'spendCategoryIds',
};

export const SPEND_CONTROL_V2_FORM_CONFIG = {
  [SPEND_CONTROL_V2_FORM_KEYS.cardStatus]: {
    label: $localize`Card status`,
    requestKey: 'credit_frozen',
    responseKey: 'creditFrozen',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
    options: [
      { key: 'false', label: $localize`Active` },
      { key: 'true', label: $localize`Frozen` },
    ],
  },
  [SPEND_CONTROL_V2_FORM_KEYS.creditLimitPerDay]: {
    label: $localize`Per day`,
    placeholder: '--',
    requestKey: 'daily_spend_limit_in_cents',
    responseKey: 'dailySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.creditLimitPerWeek]: {
    label: $localize`Per week (Sun-Sat)`,
    placeholder: '--',
    requestKey: 'weekly_spend_limit_in_cents',
    responseKey: 'weeklySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.creditLimitPerMonth]: {
    label: $localize`Per calendar month`,
    placeholder: '--',
    requestKey: 'monthly_spend_limit_in_cents',
    responseKey: 'monthlySpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.creditLimit]: {
    label: $localize`Per billing cycle`,
    placeholder: '--',
    requestKey: 'billing_cycle_spend_limit_in_cents',
    responseKey: 'billingCycleSpendLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.transactionCap]: {
    label: $localize`Per transaction`,
    placeholder: '--',
    requestKey: 'transaction_limit_in_cents',
    responseKey: 'transactionLimitInCents',
    type: FORM_ELEMENT_KEYS.NUMBER,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.timeLimit]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: TIME_LIMIT_OPTIONS.everyday,
        label: $localize`Everyday`,
        description: $localize`Cards will work at all times.`,
      },
      {
        key: TIME_LIMIT_OPTIONS.custom,
        label: $localize`Custom`,
        description: $localize`Cards will only work during a set schedule.`,
      },
    ],
  },
  [SPEND_CONTROL_V2_FORM_KEYS.enableDays]: {
    requestKey: 'enable_days',
    responseKey: 'enableDays',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.enableStartTime]: {
    requestKey: 'enable_start_time',
    responseKey: 'enableStartTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.enableEndTime]: {
    requestKey: 'enable_end_time',
    responseKey: 'enableEndTime',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
  },
  [SPEND_CONTROL_V2_FORM_KEYS.spendCategory]: {
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: SPEND_CATEGORY_OPTIONS.all,
        label: $localize`Allow all categories`,
      },
      {
        key: SPEND_CATEGORY_OPTIONS.specific,
        label: $localize`Allow specific categories`,
      },
    ],
  },
  [SPEND_CONTROL_V2_FORM_KEYS.spendCategoryIds]: {
    requestKey: 'spend_category_ids',
    responseKey: 'spendCategoryIds',
    type: FORM_ELEMENT_KEYS.MULTI_SELECT,
  },
};

export const MESSAGES = {
  UPDATE_SPEND_CONTROL_SUCCESS: $localize`Card spend control successfully updated`,
  UPDATE_SPEND_CONTROL_ERROR: $localize`Something went wrong`,
  CREATE_SPEND_CONTROL_SUCCESS: $localize`Card spend control successfully created`,
  UPDATE_SPEND_PROFILE_SUCCESS: $localize`Profile has been saved`,
  UPDATE_SPEND_PROFILE_ERROR: $localize`Something went wrong`,
  CREATE_SPEND_PROFILE_SUCCESS: $localize`Profile has been created`,
  CREATE_DUPLICATE_SPEND_PROFILE_SUCCESS: $localize`Profile has been duplicated`,
  NOT_EMPTY: $localize`cannot be empty`,
  INVALID: $localize`is invalid`,
  FORM_HAS_ERRORS: $localize`Please correct the errors on the page before saving`,
  SECTION_UPDATED_SUCCESSFULLY: $localize` successfully updated`,
  OVERRIDE_CREATED_SUCCESSFULLY: $localize`Override has been set`,
  OVERRIDE_UPDATED_SUCCESSFULLY: $localize`Override has been edited`,
};


export const DAYS_OF_WEEK: IDayOfWeek[] = [
  {
    id: 'SUN',
    label: $localize`Sunday`,
    shortLabel: $localize`Sun`,
    selected: false,
  },
  {
    id: 'MON',
    label: $localize`Monday`,
    shortLabel: $localize`Mon`,
    selected: true,
  },
  {
    id: 'TUE',
    label: $localize`Tuesday`,
    shortLabel: $localize`Tue`,
    selected: true,
  },
  {
    id: 'WED',
    label: $localize`Wednesday`,
    shortLabel: $localize`Wed`,
    selected: true,
  },
  {
    id: 'THU',
    label: $localize`Thursday`,
    shortLabel: $localize`Thu`,
    selected: true,
  },
  {
    id: 'FRI',
    label: $localize`Friday`,
    shortLabel: $localize`Fri`,
    selected: true,
  },
  {
    id: 'SAT',
    label: $localize`Saturday`,
    shortLabel: $localize`Sat`,
    selected: false,
  },
];

export const NO_SPEND_CONTROL_TEXT = $localize`Spend controls have not been set.`;

export const NO_SPEND_CONTROL_TEXT_V2 = $localize`Limits have not been set.`;

export const NO_FRAUD_CONTROL_COMPANY_TEXT = $localize`Fraud controls are disabled for all cardholders.`;

export const NO_FRAUD_CONTROL_COMPANY_TEXTV2 = $localize`Mobile-Based Unlock is disabled for all cardholders.`;

export const NO_DRIVER_FRAUD_CONTROL_COMPANY_TEXT = $localize`Mobile-Based Unlock is disabled for cards assigned to drivers.`;

export const NO_VEHICLE_FRAUD_CONTROL_COMPANY_TEXT = $localize`Mobile-Based Unlock is disabled for cards assigned to vehicles.`;

export const TOOLTIP_TIME_FORMAT = {
  input: 'hh:mm',
  output: 'h:mm A',
};

export const TOOLTIP_LIMITED_ACCESS_REASONS_KEYS = {
  edit: 'edit',
  delete: 'delete',
  setAsDefault: 'make_default',
  generic: 'generic',
};

export const TOOLTIP_LIMITED_ACCESS_REASONS = {
  [TOOLTIP_LIMITED_ACCESS_REASONS_KEYS.edit]: {
    missing_permissions : $localize`You don’t have the permissions required to edit this profile.`,
    restricted_group_access: $localize`You can’t edit this profile because it’s been applied to cardholders that you don’t have access to.`,
  },
  [TOOLTIP_LIMITED_ACCESS_REASONS_KEYS.delete]: {
    missing_permissions : $localize`You don’t have the permissions required to delete this profile.`,
    restricted_group_access: $localize`You can’t delete this profile because it’s been applied to cardholders that you don’t have access to.`,
  },
  [TOOLTIP_LIMITED_ACCESS_REASONS_KEYS.setAsDefault]: {
    missing_permissions : $localize`You don’t have the permissions required to set this profile as default.`,
  },
  [TOOLTIP_LIMITED_ACCESS_REASONS_KEYS.generic]: {
    missing_permissions :  $localize`You don’t have the required permissions.`,
  },
};

export const ACTIVATE_CARD_MODAL_PENDO_EVENT = 'Activate Card Modal New Spend Profile Added';

export const DEFAULT_CASH_ADVANCES_MAX_DAILY_WITHDRAWAL_LIMIT: number = 300;

export const CASH_ADVANCE_MOTIVE_FEES: number = 1.5;

// keep it like this to maintain order
export const SPEND_SUB_CATEGORY_LIMITS_MAP: string[][] = [
  ['dailySpendLimitInDollars', $localize`Daily limit`],
  ['weeklySpendLimitInDollars', $localize`Weekly limit`],
  ['monthlySpendLimitInDollars', $localize`Monthly limit`],
  ['billingCycleSpendLimitInDollars', $localize`Per billing cycle`],
  ['transactionLimitInDollars', $localize`Per transaction`],
];

export const SET_LIMIT_GRANULAR_SEND_CONTROL_CLICK_PENDO_EVENT = 'GRANULAR_SPEND_CONTROL_SET_LIMIT_CLICKED';

export const GRANULAR_SPEND_CONTROL_VALUE_POSTED = 'GRANULAR_SPEND_CONTROL_VALUES_SET';
